import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TrendingUp, BarChart3, Link2, Clock, Search, Activity, Plus, X, StickyNote } from 'lucide-react'
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  AreaChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts'
import { useCrawlTrends, useRankingTrends, useBacklinkTrends, useGscTrends, useGa4Trends, useCwvTrends } from '../hooks/useTrends'
import { useAnnotations, useCreateAnnotation, useDeleteAnnotation, type Annotation } from '../hooks/useAnnotations'
import { toast } from 'sonner'
import { CHART_PALETTE } from '@/client/lib/colors'

const PERIOD_OPTIONS = [
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '180d', value: 180 },
  { label: '1y', value: 365 },
]

// Colour palette for ranking lines (up to 10 keywords) — uses CSS variables for dark mode support
const RANK_COLOURS = CHART_PALETTE

function PeriodSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {PERIOD_OPTIONS.map((opt) => (
        <Button
          key={opt.value}
          variant={value === opt.value ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  )
}

function formatShortDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

/** Convert annotation dates to formatted chart labels for matching */
function annotationsForChart(annotations: Annotation[], chartDates: string[]): Annotation[] {
  const chartDateSet = new Set(chartDates)
  return annotations.filter((a) => chartDateSet.has(formatShortDate(a.annotation_date)))
}

/** Render ReferenceLine elements for annotations on a chart */
function AnnotationLines({ annotations, chartDates, yAxisId }: { annotations: Annotation[]; chartDates: string[]; yAxisId?: string }) {
  const matching = annotationsForChart(annotations, chartDates)
  return (
    <>
      {matching.map((a) => (
        <ReferenceLine
          key={a.id}
          x={formatShortDate(a.annotation_date)}
          stroke="var(--chart-purple)"
          strokeDasharray="4 4"
          strokeOpacity={0.7}
          {...(yAxisId ? { yAxisId } : {})}
          label={{ value: '\u2691', fontSize: 14, fill: 'var(--chart-purple)', position: 'top' }}
        />
      ))}
    </>
  )
}

// ── Annotations Panel ──────────────────────────────────────────

function AnnotationsPanel({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [showForm, setShowForm] = useState(false)
  const [date, setDate] = useState('')
  const [text, setText] = useState('')
  const createAnnotation = useCreateAnnotation(siteId)
  const deleteAnnotation = useDeleteAnnotation(siteId)

  const handleAdd = () => {
    if (!date || !text.trim()) return
    createAnnotation.mutate({ date, text: text.trim() }, {
      onSuccess: () => {
        setDate('')
        setText('')
        setShowForm(false)
        toast.success('Annotation added')
      },
      onError: () => toast.error('Failed to add annotation'),
    })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Annotations
          </CardTitle>
          <Button size="sm" variant="outline" className="h-7" onClick={() => setShowForm(!showForm)}>
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div className="flex items-end gap-2 mb-3">
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-36 text-xs" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Note</Label>
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. Site redesign launched"
                className="h-8 text-xs"
                maxLength={200}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>
            <Button size="sm" className="h-8" onClick={handleAdd} disabled={createAnnotation.isPending || !date || !text.trim()}>
              Save
            </Button>
          </div>
        )}

        {annotations.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {annotations.map((a) => (
              <Badge key={a.id} variant="secondary" className="text-xs gap-1 pr-1">
                <span className="text-muted-foreground">{formatShortDate(a.annotation_date)}</span>
                {a.text}
                <button
                  type="button"
                  onClick={() => deleteAnnotation.mutate(a.id, {
                    onSuccess: () => toast.success('Annotation removed'),
                  })}
                  className="ml-1 rounded-sm hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : !showForm ? (
          <p className="text-xs text-muted-foreground">No annotations yet. Add markers for site changes, redesigns, or campaigns.</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ── Crawl Trends (Score + Issues) ────────────────────────────────

function CrawlTrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(90)
  const { data, isLoading } = useCrawlTrends(siteId, days)
  const series = data?.series ?? []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Clock className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need at least 2 completed crawls to show trends.
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartData = series.map((p) => ({
    date: formatShortDate(p.date),
    score: p.score != null ? Math.round(p.score) : null,
    issues: p.issues,
    pages: p.pages,
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            SEO Score & Issues
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              yAxisId="score"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <YAxis
              yAxisId="issues"
              orientation="right"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Issues', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine yAxisId="score" y={80} stroke="var(--chart-success)" strokeDasharray="3 3" strokeOpacity={0.5} />
            <ReferenceLine yAxisId="score" y={50} stroke="var(--chart-warning)" strokeDasharray="3 3" strokeOpacity={0.5} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} yAxisId="score" />
            <Area
              yAxisId="score"
              type="monotone"
              dataKey="score"
              name="SEO Score"
              fill="var(--chart-primary)"
              fillOpacity={0.15}
              stroke="var(--chart-primary)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
            <Bar
              yAxisId="issues"
              dataKey="issues"
              name="Issues"
              fill="var(--chart-error)"
              fillOpacity={0.6}
              radius={[2, 2, 0, 0]}
              maxBarSize={20}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Ranking Trends ───────────────────────────────────────────────

function RankingTrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(90)
  const { data, isLoading } = useRankingTrends(siteId, days)
  const series = data?.series ?? []
  const keywords = data?.keywords ?? []
  const summary = data?.summary

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (!keywords.length || series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <TrendingUp className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need tracked keywords with at least 2 data points to show ranking trends.
          </p>
        </CardContent>
      </Card>
    )
  }

  // Show top 5 keywords by default, rest available via legend click
  const topKeywords = keywords.slice(0, 5)

  const chartData = series.map((p) => ({
    date: formatShortDate(p.date),
    ...p.positions,
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Ranking Trends
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
        {summary && (
          <div className="flex gap-4 text-xs text-muted-foreground mt-1">
            <span>Avg: {summary.averagePosition != null ? summary.averagePosition.toFixed(1) : '-'}</span>
            <span className="text-green-600 dark:text-green-400">Top 3: {summary.keywordsInTop3}</span>
            <span className="text-blue-600 dark:text-blue-400">Top 10: {summary.keywordsInTop10}</span>
            <span className="text-yellow-600 dark:text-yellow-400">Top 20: {summary.keywordsInTop20}</span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              reversed
              domain={[1, 100]}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Position', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={3} stroke="var(--chart-success)" strokeDasharray="3 3" strokeOpacity={0.3} label={{ value: 'Top 3', fontSize: 10 }} />
            <ReferenceLine y={10} stroke="var(--chart-primary)" strokeDasharray="3 3" strokeOpacity={0.3} label={{ value: 'Top 10', fontSize: 10 }} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} />
            {topKeywords.map((kw, i) => (
              <Line
                key={kw}
                type="monotone"
                dataKey={kw}
                name={kw.length > 25 ? kw.slice(0, 22) + '...' : kw}
                stroke={RANK_COLOURS[i % RANK_COLOURS.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Backlink Trends ──────────────────────────────────────────────

function BacklinkTrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(180)
  const { data, isLoading } = useBacklinkTrends(siteId, days)
  const series = data?.series ?? []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Link2 className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need at least 2 backlink snapshots to show growth trends.
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartData = series.map((p) => ({
    date: formatShortDate(p.date),
    backlinks: p.totalBacklinks,
    domains: p.referringDomains,
    rank: p.domainRank,
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Backlink Growth
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} />
            <Area
              type="monotone"
              dataKey="backlinks"
              name="Total Backlinks"
              fill="var(--chart-primary)"
              fillOpacity={0.15}
              stroke="var(--chart-primary)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="domains"
              name="Referring Domains"
              fill="var(--chart-success)"
              fillOpacity={0.15}
              stroke="var(--chart-success)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── GSC Performance Trends ───────────────────────────────────────

function GscTrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(180)
  const { data, isLoading } = useGscTrends(siteId, days)
  const series = data?.series ?? []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Search className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need at least 2 GSC snapshots to show performance trends.
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartData = series.map((p) => ({
    date: formatShortDate(p.snapshot_date),
    clicks: p.total_clicks,
    impressions: p.total_impressions,
    position: p.avg_position,
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            GSC Performance
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              yAxisId="traffic"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Traffic', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <YAxis
              yAxisId="position"
              orientation="right"
              reversed
              domain={[1, 'auto']}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Avg Position', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} yAxisId="traffic" />
            <Bar
              yAxisId="traffic"
              dataKey="clicks"
              name="Total Clicks"
              fill="var(--chart-primary)"
              fillOpacity={0.7}
              radius={[2, 2, 0, 0]}
              maxBarSize={20}
            />
            <Bar
              yAxisId="traffic"
              dataKey="impressions"
              name="Total Impressions"
              fill="var(--chart-success)"
              fillOpacity={0.3}
              radius={[2, 2, 0, 0]}
              maxBarSize={20}
            />
            <Line
              yAxisId="position"
              type="monotone"
              dataKey="position"
              name="Avg Position"
              stroke="var(--chart-warning)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── GA4 Traffic Trends ────────────────────────────────────────────

function Ga4TrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(180)
  const { data, isLoading } = useGa4Trends(siteId, days)
  const series = data?.trends ?? []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Activity className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need at least 2 GA4 snapshots to show traffic trends. Enable daily collection on the Analytics tab.
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartData = series.map((p) => ({
    date: formatShortDate(p.snapshot_date),
    sessions: p.sessions,
    pageViews: p.page_views,
    bounceRate: Math.round(p.bounce_rate * 100),
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            GA4 Traffic
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              yAxisId="traffic"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Traffic', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <YAxis
              yAxisId="bounce"
              orientation="right"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Bounce %', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} yAxisId="traffic" />
            <Area
              yAxisId="traffic"
              type="monotone"
              dataKey="sessions"
              name="Sessions"
              fill="var(--chart-primary)"
              fillOpacity={0.15}
              stroke="var(--chart-primary)"
              strokeWidth={2}
            />
            <Area
              yAxisId="traffic"
              type="monotone"
              dataKey="pageViews"
              name="Pageviews"
              fill="var(--chart-success)"
              fillOpacity={0.1}
              stroke="var(--chart-success)"
              strokeWidth={2}
            />
            <Line
              yAxisId="bounce"
              type="monotone"
              dataKey="bounceRate"
              name="Bounce Rate %"
              stroke="var(--chart-warning)"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Core Web Vitals Trends ───────────────────────────────────────

function CwvTrendsChart({ siteId, annotations }: { siteId: string; annotations: Annotation[] }) {
  const [days, setDays] = useState(180)
  const { data, isLoading } = useCwvTrends(siteId, days)
  const series = data?.trends ?? []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  if (series.length < 2) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Activity className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Need Lighthouse data from at least 2 crawls to show CWV trends.
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartData = series.map((p) => ({
    date: formatShortDate(p.crawl_date),
    performance: Math.round(p.avg_performance),
    lcp: Math.round(p.avg_lcp),
    cls: p.avg_cls,
    pages: p.pages_measured,
  }))

  const chartDates = chartData.map((d) => d.date)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Core Web Vitals
          </CardTitle>
          <PeriodSelector value={days} onChange={setDays} />
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
            <YAxis
              yAxisId="score"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'Score', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <YAxis
              yAxisId="lcp"
              orientation="right"
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              label={{ value: 'LCP (ms)', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              labelStyle={{ fontWeight: 600 }}
              formatter={((value: number, name: string) => {
                if (name === 'LCP') return [`${value.toLocaleString()} ms`, name]
                return [value, name]
              }) as never}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine yAxisId="score" y={90} stroke="var(--chart-success)" strokeDasharray="3 3" strokeOpacity={0.4} />
            <ReferenceLine yAxisId="lcp" y={2500} stroke="var(--chart-warning)" strokeDasharray="3 3" strokeOpacity={0.4} />
            <AnnotationLines annotations={annotations} chartDates={chartDates} yAxisId="score" />
            <Area
              yAxisId="score"
              type="monotone"
              dataKey="performance"
              name="Performance"
              fill="var(--chart-success)"
              fillOpacity={0.15}
              stroke="var(--chart-success)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
            <Line
              yAxisId="lcp"
              type="monotone"
              dataKey="lcp"
              name="LCP"
              stroke="var(--chart-warning)"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

// ── Main TrendsTab Component ─────────────────────────────────────

export function TrendsTab({ siteId }: { siteId: string }) {
  const { data: annotationData } = useAnnotations(siteId)
  const annotations = annotationData?.annotations ?? []

  return (
    <div className="space-y-6">
      <AnnotationsPanel siteId={siteId} annotations={annotations} />
      <CrawlTrendsChart siteId={siteId} annotations={annotations} />
      <CwvTrendsChart siteId={siteId} annotations={annotations} />
      <GscTrendsChart siteId={siteId} annotations={annotations} />
      <Ga4TrendsChart siteId={siteId} annotations={annotations} />
      <RankingTrendsChart siteId={siteId} annotations={annotations} />
      <BacklinkTrendsChart siteId={siteId} annotations={annotations} />
    </div>
  )
}
